import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getTool, TOOLS } from "@/components/tools/registry";
import { Reveal } from "@/components/motion/primitives";
import AffiliateStrip from "@/components/site/AffiliateStrip";
import AdSlot from "@/components/site/AdSlot";
import AdSense from "@/components/site/AdSense";
import { ArrowLeft, ArrowUpRight, Sparkles } from "lucide-react";
import { getToolSpec } from "@/lib/api";
import GenericToolRunner from "@/components/tools/GenericToolRunner";

export default function ToolDetail() {
  const { slug } = useParams();
  const tool = getTool(slug);
  const [spec, setSpec] = useState(null);
  const [specMissing, setSpecMissing] = useState(false);

  useEffect(() => {
    if (!tool) {
      setSpec(null);
      setSpecMissing(false);
      getToolSpec(slug).then(setSpec).catch(() => setSpecMissing(true));
    }
  }, [slug, tool]);

  // AI-built tool from the daily Tool Builder engine — rendered by the generic runner.
  if (!tool) {
    if (spec) {
      return (
        <div data-testid="tool-detail-page">
          <section className="pt-32 md:pt-40 pb-8 border-b border-slate-100 grid-lines">
            <div className="mx-auto max-w-4xl px-6 md:px-12">
              <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 mb-6"><ArrowLeft className="w-4 h-4" /> All tools</Link>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center"><Sparkles className="w-7 h-7 text-indigo-600" strokeWidth={1.5} /></div>
                <div>
                  <h1 className="font-display text-3xl md:text-4xl tracking-tight">{spec.name}</h1>
                  <p className="text-slate-600 mt-1">{spec.desc}</p>
                </div>
              </div>
            </div>
          </section>
          <section className="py-12">
            <div className="mx-auto max-w-4xl px-6 md:px-12">
              <Reveal>
                <div className="bg-white rounded-2xl border border-slate-100 soft-shadow p-6 md:p-8">
                  <GenericToolRunner spec={spec} />
                </div>
              </Reveal>
              <div className="mt-10 space-y-8" data-testid="tool-ad-slots">
                <AdSense />
                <AdSlot placement="sidebar" />
              </div>
            </div>
          </section>
        </div>
      );
    }
    if (!specMissing) {
      return <div className="pt-40 pb-40 text-center text-slate-400">Loading tool…</div>;
    }
    return (
      <div className="pt-40 pb-40 text-center">
        <p className="font-display text-4xl">Tool not found.</p>
        <Link to="/tools" className="text-indigo-600 mt-4 inline-block link-underline">Back to Tools</Link>
      </div>
    );
  }

  const Comp = tool.Component;
  const others = TOOLS.filter((t) => t.slug !== slug).slice(0, 3);

  return (
    <div data-testid="tool-detail-page">
      <section className="pt-32 md:pt-40 pb-8 border-b border-slate-100 grid-lines">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 mb-6"><ArrowLeft className="w-4 h-4" /> All tools</Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center"><tool.icon className="w-7 h-7 text-indigo-600" strokeWidth={1.5} /></div>
            <div>
              <h1 className="font-display text-3xl md:text-4xl tracking-tight">{tool.name}</h1>
              <p className="text-slate-600 mt-1">{tool.desc}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal>
            <div className="bg-white rounded-2xl border border-slate-100 soft-shadow p-6 md:p-8">
              <Comp />
            </div>
          </Reveal>
          <Link to={`/blog/${tool.slug}-guide`} data-testid="tool-guide-link"
            className="mt-6 flex items-center justify-between gap-4 border border-slate-200 rounded-xl px-5 py-4 bg-white hover:border-indigo-300 transition group">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-indigo-600">From the blog</p>
              <p className="text-sm font-medium mt-1 group-hover:text-indigo-600 transition-colors">{tool.name}: What It Is and Who It's For — read the guide</p>
            </div>
            <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 flex-shrink-0" />
          </Link>
          {/* Monetization: Google AdSense + tracked house/affiliate ads on every tool. */}
          <div className="mt-10 space-y-8" data-testid="tool-ad-slots">
            <AdSense />
            <AdSlot placement="sidebar" />
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <div className="mb-16">
            <AffiliateStrip variant="grid" limit={3} title="Tools that pair well with this" subtitle="Recommendations from Julius — some are affiliate links." />
          </div>
          <p className="overline mb-5">More tools</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {others.map((o) => (
              <Link key={o.slug} to={`/tools/${o.slug}`} className="group border border-slate-100 rounded-xl p-4 bg-white hover-lift">
                <div className="flex items-center justify-between"><o.icon className="w-5 h-5 text-indigo-600" /><ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600" /></div>
                <p className="font-medium mt-3 text-sm group-hover:text-indigo-600 transition-colors">{o.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
